import React, { useState } from 'react';
import PropTypes from 'prop-types';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Accordion, AccordionSummary, AccordionDetails, Box, Typography } from '@mui/material';

import moment from 'moment';

import currencyHelper from '../../../helpers/currencyHelper';
import SilverBoomReport from '../../../resources/images/logos/SilverBoom Report.svg';

import HistoricDataChart from './historicDataChart';
import Loader from '../loader';

const ProductCard = ({ account, historicData = [], loadingHistoricData }) => {
  const [expandedStrategies, setExpandedStrategies] = useState({});
  const currency = currencyHelper(account?.ccy || 'ZAR');

  const balances = Array.isArray(account?.balances) ? account.balances : [];

  const grouped = balances.reduce((acc, item) => {
    const key = item.strategy || '__NO_STRATEGY__';

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(item);

    return acc;
  }, {});

  const strategyGroups = Object.entries(grouped).filter(
    ([strategy]) => strategy !== '__NO_STRATEGY__'
  );

  const noStrategyItems = grouped.__NO_STRATEGY__ || [];

  return (
    <Accordion
      disableGutters
      elevation={0}
      TransitionProps={{
        timeout: {
          enter: 450,
          exit: 350
        }
      }}
      sx={{
        backgroundColor: (theme) => theme.palette.grey[200],
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'background-color 350ms ease',
        '&:before': {
          display: 'none'
        },
        '&.Mui-expanded': {
          backgroundColor: '#FBF0EA'
        }
      }}>
      <AccordionSummary
        expandIcon={
          <Box
            component="img"
            src={SilverBoomReport}
            alt="expand"
            sx={{
              width: 30,
              height: 30
            }}
          />
        }
        sx={{
          px: 3,
          py: 1.5,

          transition: 'padding 350ms ease',

          '& .MuiAccordionSummary-content': {
            margin: 0,
            transition: 'margin 350ms ease'
          },

          '& .MuiAccordionSummary-expandIconWrapper': {
            transform: 'rotate(90deg)',
            transition: 'transform 350ms ease'
          },

          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'rotate(-90deg)'
          }
        }}>
        <Box width="100%" display="flex" justifyContent="space-between">
          <Box>
            <Typography
              sx={{
                color: '#C56A2D',
                fontWeight: 700,
                fontSize: 12,
                textTransform: 'uppercase'
              }}>
              {account.product} - {account.shortName || account.accountCode}
            </Typography>

            <Typography variant="caption" display="block">
              Administered by {account.source?.name}
            </Typography>

            <Typography variant="caption" display="block">
              {account.productName}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              display="block"
              sx={{
                marginRight: '4px'
              }}>
              Date: {account?.priceDate ? moment(account.priceDate).format('DD MMM YYYY') : '-'}
            </Typography>

            <Typography
              sx={{
                color: '#C56A2D',
                fontWeight: 700,
                fontSize: 28,
                mt: 1
              }}>
              {currency}{' '}
              {Number(account.nav || 0).toLocaleString('en-ZA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          px: 3,
          py: 2
        }}>
        {/* NO STRATEGY ITEMS */}
        {noStrategyItems.map((balance, index) => (
          <React.Fragment key={`nostrategy-${index}`}>
            <Box py={1}>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Box flex={1}>
                  <Typography variant="caption">
                    {balance.assetClass === 'CASH' ? 'Cash' : balance.instrument}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="flex-end" minWidth={180}>
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Typography variant="caption">
                      Date:{' '}
                      {balance?.priceDate ? moment(balance.priceDate).format('DD MMM YYYY') : '-'}
                    </Typography>

                    <Typography fontWeight={600}>
                      {currency}{' '}
                      {Number(balance.closePositionValueVal || 0).toLocaleString('en-ZA', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </React.Fragment>
        ))}

        {/* STRATEGY GROUPS */}
        {strategyGroups.map(([strategy, items], strategyIndex) => {
          const total = items.reduce(
            (sum, balance) => sum + (balance.closePositionValueVal || 0),
            0
          );

          const expanded = !!expandedStrategies[strategy];

          return (
            <Accordion
              key={`${strategy}-${strategyIndex}`}
              expanded={expanded}
              onChange={() =>
                setExpandedStrategies((prev) => ({
                  ...prev,
                  [strategy]: !prev[strategy]
                }))
              }
              disableGutters
              elevation={0}
              sx={{
                backgroundColor: 'transparent',
                boxShadow: 'none',
                '&:before': {
                  display: 'none'
                }
              }}>
              <AccordionSummary
                sx={{
                  px: 0,
                  minHeight: 'unset',
                  '& .MuiAccordionSummary-content': {
                    margin: '8px 0'
                  }
                }}>
                <Box width="100%" display="flex" justifyContent="space-between">
                  <Typography variant="caption">{strategy}</Typography>

                  <Box display="flex" justifyContent="flex-end" minWidth={180}>
                    <Box display="flex" alignItems="center" width="100%">
                      <Typography
                        fontWeight={600}
                        sx={{
                          flex: 1,
                          textAlign: 'left'
                        }}>
                        {currency}{' '}
                        {total.toLocaleString('en-ZA', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </Typography>

                      <Box
                        sx={{
                          width: 20,
                          display: 'flex',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                        {expanded ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails
                sx={{
                  px: 0,
                  py: 0
                }}>
                {items.map((balance, index) => (
                  <React.Fragment key={index}>
                    <Box py={1} pl={3}>
                      <Box display="flex" justifyContent="space-between" gap={2}>
                        <Box flex={1}>
                          <Typography variant="caption">
                            {balance.assetClass === 'CASH' ? 'Cash' : balance.instrument}
                          </Typography>
                        </Box>

                        <Box minWidth={180} alignItems="flex-end">
                          <Typography variant="caption">
                            Date:{' '}
                            {balance?.priceDate
                              ? moment(balance.priceDate).format('DD MMM YYYY')
                              : '-'}
                          </Typography>

                          <Typography fontWeight={600}>
                            {currency}{' '}
                            {Number(balance.closePositionValueVal || 0).toLocaleString('en-ZA', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </React.Fragment>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })}

        {/* HISTORIC DATA CHART */}
        <Box mt={3} pt={2} borderTop="1px solid rgba(0, 0, 0, 0.12)">
          {loadingHistoricData ? (
            <Loader loading={true} message="Loading historic data" />
          ) : (
            <HistoricDataChart data={historicData} currency={currency} />
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

ProductCard.propTypes = {
  account: PropTypes.object.isRequired,
  historicData: PropTypes.array
};

export default ProductCard;
